// SignInPage — unauthenticated landing page.
// RequireAuth stashes the attempted URL in location.state.from; we thread it
// through to the backend as ?return_to= so WorkOS redirects back after auth.

import { useLocation } from "react-router-dom";
import { LoginButton } from "@avagency/frontend/components/auth/LoginButton";
import { Footer } from "../components/Footer";

interface LocationState {
  from?: { pathname: string; search?: string; hash?: string };
}

function buildSignInUrl(returnTo: string): string {
  const url = new URL("/api/v1/auth/signin", window.location.origin);
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

export function SignInPage() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const from = state?.from;
  const returnTo = from
    ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
    : "/";
  const signInUrl = buildSignInUrl(returnTo);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-4">
      <div className="w-full max-w-sm">
        {/* Brand */}
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <div className="yg-signin-logo" aria-hidden="true" />
          <h1 className="yg-signin-title">Yahoogle</h1>
          <p className="yg-signin-sub">
            Email compliance auditor — SPF, DKIM,<br className="hidden sm:inline" /> DMARC &amp; complaint-rate tracking.
          </p>
        </div>

        {/* Sign-in card */}
        <div className="yg-signin-card">
          <span className="yg-signin-label">Sign in to your account</span>
          <LoginButton startUrl={signInUrl} className="w-full">
            Continue with magic link
          </LoginButton>
          <p className="mt-4 text-center text-xs" style={{ color: "var(--yg-silver-3)" }}>
            We'll email you a secure, one-time sign-in link.
          </p>
        </div>

        {/* Features strip */}
        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          {[
            { label: "SPF / DKIM", sub: "Record checks" },
            { label: "DMARC", sub: "Policy audit" },
            { label: "Complaints", sub: "Rate tracking" },
          ].map((f) => (
            <div
              key={f.label}
              className="rounded-xl border py-3 px-2"
              style={{
                borderColor: "var(--yg-line)",
                background: "rgba(10,15,28,0.5)",
              }}
            >
              <div
                className="text-xs font-semibold"
                style={{ color: "var(--yg-silver-0)", letterSpacing: "-0.01em" }}
              >
                {f.label}
              </div>
              <div
                className="mt-0.5 text-xs"
                style={{
                  color: "var(--yg-silver-3)",
                  fontFamily: "var(--yg-font-mono)",
                  fontSize: "10px",
                  letterSpacing: "0.06em",
                }}
              >
                {f.sub}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
}
